import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  CustomDeck,
  CustomDeckGroup,
  DocumentAccessLevel,
  EditMutex,
  FolderItemType,
  PageSettingOverride,
  Page,
} from '@alucio/aws-beacon-amplify/src/models';
import isEqual from 'lodash/isEqual'
import isEmpty from 'lodash/isEmpty';
import { batch } from 'react-redux';
import { DNAModalActions } from 'src/state/redux/slice/DNAModal/DNAModal';
import ClearSlides from 'src/components/DNA/Modal/DNAPresentationBuilder/ClearSlides'
import useEditorState from './useEditorState';
import RequiredSlidesHiddenConfirmation
  from 'src/components/DNA/Modal/DNAPresentationBuilder/RequiredSlidesHiddenConfirmation';
import CloseConfirmation from 'src/components/DNA/Modal/DNAPresentationBuilder/CloseConfirmation';
import {
  PRESENTATION_BUILDER_VARIANT,
  presentationBuilderActions,
  EDITOR_TYPE,
} from 'src/state/redux/slice/PresentationBuilder/PresentationBuilder';
import { folderActions } from 'src/state/redux/slice/folder';
import useSelectorState from './useSelectorState';
import { ThumbnailDimensions, ThumbnailSize } from 'src/hooks/useThumbnailSize/useThumbnailSize';
import { RootState, useDispatch, useSelector } from 'src/state/redux';
import {
  CustomDeckGroupORM,
  CustomDeckORM,
  DocumentVersionORM,
  FolderItemORM,
  FolderORM,
  ORMTypes,
  VERSION_UPDATE_STATUS,
} from 'src/types/types';
import { useCustomDeckORMById } from 'src/state/redux/selector/folder';
import { v4 as uuid } from 'uuid';
import { contentPreviewModalActions } from '../../../state/redux/slice/contentPreviewModal';
import { customDeckActions } from 'src/state/redux/slice/customDeck';
import { userNotationsActions } from 'src/state/redux/slice/userNotations';
import { userDocumentVersionSettingsActions } from 'src/state/redux/slice/userDocumentVersionSettings';
import { useCurrentUser } from 'src/state/redux/selector/user';
import { useAllDocumentVersionMap, selUserDocumentVersionSettingsMap } from 'src/state/redux/selector/document';
import useCurrentPage from 'src/components/DNA/hooks/useCurrentPage';
import { useAppSettings } from 'src/state/context/AppSettings';
import { ThumbnailPage } from 'src/components/SlideSelector/SlideSelector';
import SelectFolder from '../SelectFolderPresentationBuilder';
import { UpdateType, sharedFolderActions } from 'src/state/redux/slice/sharedFolder';
import { isSharedEditableFolder } from 'src/utils/foldersHelpers';
import { GroupDraftStatus, ReplaceGroupDraft } from '../PresentationEditor/FindReplacement/FindReplacementProvider';
import * as logger from 'src/utils/logger';
import { ContentPageData, getS3KeysWithinCustomDeck } from 'src/hooks/useContentPageData/useContentPageData';
import { fetchPagesJson } from 'src/utils/loadCloudfrontAsset/common';
import { getCurrentDocVer } from 'src/state/machines/sharing/shareUtils';
import { detectArchivedFileKeyPath } from 'src/components/SlideSelector/useThumbnailSelector';
import {
  assignReplacementGroups,
  getValidMappedSingleSlidesGroup,
  isValidMinorUpdatedMultiSlidesGroup,
} from 'src/state/machines/findReplacement/findReplacementUtils';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import { SidebarOptions } from 'src/components/ContentPreviewModalV2/SideBar/SidebarProvider';
import {
  getCustomDeckCurrentPageSettingOverrides,
  getCustomDeckInheritedPageSettingOverrides,
  getCustomDeckDefaultGroupVisibility,
  getMergedCustomDeckPageSettingOverrides,
  getPageWithHiddenSlideSettingsState,
} from 'src/utils/hiddenSlides';
import {
  canRemoveGroup as canRemoveGroupRule,
  canSaveGroups,
  hasRequiredHiddenSlides,
  isSingleAndHasAssociation as isSingleAndHasAssociationRule,
} from 'src/utils/customDeckRules/editing';
import { getPayloadGroupStatus } from 'src/utils/customDeckRules/eligibility';
import { getAssociatedParentIds, payloadGroupsToCustomDeckGroups } from 'src/utils/customDeckRules/payload';
import { GroupStatus } from 'src/utils/customDeckRules/groupStatus';
import { useHistory } from 'src/router';
import { ROUTES } from 'src/router/routeDef';
import { buildAISearchDeckDraft, resolveAISearchDeckReferences } from 'src/utils/customDeckRules/aiSearch';
import { saveAIDeck, AIDeckSaveSession } from 'src/state/redux/slice/PresentationBuilder/saveAIDeck';
import { validatePayloadGroups as validatePayloadGroupsRule } from 'src/utils/customDeckRules/validation';
import { shouldNavigateToFoldersAfterAISearchClose } from './aiSearchNavigation';

export { GroupStatus } from 'src/utils/customDeckRules/groupStatus';
export { getPayloadGroupStatus } from 'src/utils/customDeckRules/eligibility';

/** These options represent the different 'modes' this component can be in. The default is 'selection' mode */
enum BuilderModes {
  selection,
  customization,
  replace,
}

const TIME_LAST_SEEN_MINUTES = 15
const MUTEX_INTERVAL = 60 * 1000

export type BuilderMode = keyof typeof BuilderModes

/** State properties which can be used by various sub-components to manipulate the current in-progress presentation */
export interface PresentationBuilderStateType {
  isSaving: boolean;
  saveError?: string;
  clearSlides: () => void,
  closePresentationBuilder: (save?: boolean) => void,
  savePresentation: () => void,
  saveFindReplacement: (
    updatedPayloadGroups: PayloadGroup[],
    replaceGroupDraft: ReplaceGroupDraft[],
    callBack?: () => void,
  ) => void,
  builderMode: BuilderMode,
  setBuilderMode: React.Dispatch<React.SetStateAction<BuilderMode>>,
  title: string | undefined,
  setTitle: React.Dispatch<React.SetStateAction<string | undefined>>,
  saveAttemptedWithoutTitle: boolean,
  setSaveAttemptedWithoutTitle: React.Dispatch<React.SetStateAction<boolean>>,
  numOfRequiredSlides: number,
  setNumOfRequiredSlides: React.Dispatch<React.SetStateAction<number>>,
  selectedGroups: PayloadGroup[],
  setSelectedGroups: React.Dispatch<React.SetStateAction<PayloadGroup[]>>,
  toggleHiddenPage: (groupId: string, pageId: string) => void,
  toggleGroupVisibility: (groupId: string) => void,
  visibleGroupsMap: Record<string, boolean>,
  associatedParentsMap: AssociatedParentsMap,
  setAssociatedParentsMap: React.Dispatch<React.SetStateAction<AssociatedParentsMap>>,
  hiddenSlidesVisible: boolean,
  setHiddenSlidesVisible: React.Dispatch<React.SetStateAction<boolean>>,
  showReviewRequiredOnly: boolean,
  setShowReviewRequiredOnly: React.Dispatch<React.SetStateAction<boolean>>,
  displayPageSourceFile: boolean,
  setDisplayPageSourceFile: React.Dispatch<React.SetStateAction<boolean>>,
  handleModeChange: (nextMode?: BuilderMode) => void,
  editorThumbnailSize: ThumbnailSize,
  setEditorThumbnailSize: React.Dispatch<React.SetStateAction<ThumbnailSize>>,
  cycleEditorThumbnailSize: () => void,
  editorThumbnailDimensions: ThumbnailDimensions,
  selectorThumbnailSize: ThumbnailSize,
  setSelectorThumbnailSize: React.Dispatch<React.SetStateAction<ThumbnailSize>>,
  cycleSelectorThumbnailSize: () => void,
  selectorThumbnailDimensions: ThumbnailDimensions,
  onPreview: (pageNumber?: number, openedTab?: SidebarOptions) => void,
  cancelPresentation: () => void,
  activeReplacementGroup?: PayloadGroup,
  setActiveReplacementGroup: React.Dispatch<React.SetStateAction<PayloadGroup | undefined>>,
  onApplyFindAndReplace: (payloadGroups: PayloadGroup[], newTitle?: string) => ExtendedPagePayloadGroup[],
  customDeck?: CustomDeckORM,
  editorType?: EDITOR_TYPE,

  /** TODO: These three properties seem closely interrelated, should investigate the posibility of consolidation */
  variant?: PRESENTATION_BUILDER_VARIANT,
  isCustomDeckLockedByOthers?: boolean,
  isLocked?: boolean,

  selectedTargetItems: string[],
  setSelectedTargetItems: React.Dispatch<React.SetStateAction<string[]>>,
  disablePreview: boolean,
  setDisablePreivew: React.Dispatch<React.SetStateAction<boolean>>,
  showUnavailableMessage: boolean,
  setShowUnavailableMessage: React.Dispatch<React.SetStateAction<boolean>>,
  findReplacementVisible: boolean,
  setFindReplacementVisible: React.Dispatch<React.SetStateAction<boolean>>,
  openFindReplacement: (group: ModifiedPayloadGroup) => void,
  hasNeedReviewGroup: boolean,
  compliancePanelVisible: boolean,
  setCompliancePanelVisible: React.Dispatch<React.SetStateAction<boolean>>,

  onRemoveGroup: (group: PayloadGroup) => void,
  onRemoveAllUnavailableGroups: () => void,
  onSeparateGroup: (group: PayloadGroup) => void,
  isSingleAndHasAssociation: (group: ModifiedPayloadGroup) => boolean,
  canRemoveGroup: (group: PayloadGroup) => boolean,
  targetFolder?: FolderORM,
  allContentPageDataVersions: { [documentVersionId: string]: ContentPageData[] },
  setAllContentPageDataVersions: React.Dispatch<React.SetStateAction<{
    [documentVersionId: string]: ContentPageData[];
}>>
  getPageWithDocumentVersionHiddenState: <TPage extends Page>(
    page: TPage,
    documentVersionORM?: DocumentVersionORM,
  ) => TPage,
  acceptAutoUpdateSlides: () => void,
  autoUpdateMapping: Record<string, string>,
  isMinorUpdate: boolean,
  // [Note]: BEAC-6694, disable text insert from custom deck editor for now.
  // enableSlideTextInsertion: boolean,
}

export interface PayloadGroup {
  id: string;
  groupSrcId?: string;
  documentVersionORM?: DocumentVersionORM;
  pages: Page[];
  visible: boolean;
  groupStatus: GroupStatus,
  docAccessLevel: keyof typeof DocumentAccessLevel,
  name?: string;
  locked?: boolean;
}

export type AssociatedParentsMap = Map<string, boolean>

/** NOTE: normally we'd have to use an Omit<PayloadGroup, 'pages'> here but since TS doesn't
 * care about switching from `Page` to the extended type `ThumbnailPage` it's fine */
export interface ModifiedPayloadGroup extends PayloadGroup {
  pages: ThumbnailPage[]
}

interface ExtendedPage extends Page, Pick<ThumbnailPage, 'parentIds'> { }
interface ExtendedPagePayloadGroup extends PayloadGroup {
  pages: ExtendedPage[]
}

export const unavailableStatus = {
  [GroupStatus.REVOKED]: true,
  [GroupStatus.DELETED]: true,
  [GroupStatus.ARCHIVED]: true,
}

/** Type defining config props for the <PresentationBuilderStateProvider> element */
interface PresentationBuilderStateProviderProps {
}

/** Context instantiation/config */
export const PresentationBuilderStateContext = createContext<PresentationBuilderStateType>(null!)
PresentationBuilderStateContext.displayName = 'PresentationBuilderContext'

/* When saving, convert our draft object into a CustomDeckGroup[] */
const payloadGroupsToCustomDeckGroup = (
  draftGroups: PayloadGroup[],
  existingCustomDeck?: CustomDeckORM,
) => payloadGroupsToCustomDeckGroups(draftGroups, existingCustomDeck)

/* FUNCTIONS TO CREATE A DRAFT FOLDERITEMORM TO USE THE CUSTOM DECK IN THE PLAYER */
const customDeckORMToPreview = (groups: PayloadGroup[], customDeck?: CustomDeckORM) => {
  const groupORMs: CustomDeckGroupORM[] = [];
  const validGroups = groups.filter(({ groupStatus }) => groupStatus === GroupStatus.ACTIVE);
  const customDeckGroups: CustomDeckGroup[] = payloadGroupsToCustomDeckGroup(validGroups, customDeck);
  const now = new Date().toISOString();
  const existingUserNotations = customDeck?.relations.userNotations

  validGroups.forEach((group) => {
    /** NOTE: This might be better structured as a map to reduce the overhead from iteration */
    const customDeckGroup = customDeckGroups.find(({ id }) => id === group.id)

    if (!customDeckGroup) throw (new Error('No custom deck group found, this may indicate a corruped DB entry'))

    groupORMs.push({
      isGroup: group.pages.length > 1,
      model: customDeckGroup,
      pages: group.pages.map((page) => ({
        documentVersionORM: group.documentVersionORM!,
        page,
        model: {
          documentVersionId: group.documentVersionORM?.model.id!,
          pageNumber: page.number,
          pageId: page.pageId,
        },
      })),
      meta: {
        version: {
          updateStatus: VERSION_UPDATE_STATUS.CURRENT,
        },
      },
    });
  });

  const newCustomDeckORM: CustomDeckORM = {
    model: {
      id: uuid(),
      title: customDeck?.model.title ?? '',
      createdAt: now,
      updatedAt: now,
      createdBy: '',
      autoUpdateAcknowledgedAt: now,
      updatedBy: '',
      tenantId: '',
      groups: customDeckGroups,
    },
    type: ORMTypes.CUSTOM_DECK,
    meta: {
      formattedNotations: {
        documentLevel: {},
        customDeckLevel: {},
      },
      customDeckGroups: groupORMs,
      assets: {
        isContentCached: false,
      },
      version: {
        updateStatus: VERSION_UPDATE_STATUS.CURRENT,
        requiresReview: false,
        autoUpdateUnacknowledged: false,
        withinGracePeriod: false,
      },
      permissions: {
        isCollaborator: false,
        MSLPresent: false,
        MSLShare: false,
        MSLDownload: false,
      },
      containsWebDocs: false,
      containsVideoDoc: false,
      containsHTMLDocs: false,
      hasExternalDependency: false,
    },
    relations: {
      userNotations: existingUserNotations,
    },
  }

  return newCustomDeckORM
}

/* CREATES A DRAFT FOLDER ITEM SO WE CAN SEND IT TO THE CONTENT PREVIEW MODAL AS A PREVIEW OF THE NEW CUSTOM DECK */
function toFolderItemORM(
  selectedGroups: PayloadGroup[],
  customTitle?: string, customDeckORM?: CustomDeckORM,
  folderItemORM?: FolderItemORM): FolderItemORM {
  const draftCustomDeckORM = customDeckORMToPreview(selectedGroups, customDeckORM);

  if (folderItemORM) {
    return {
      ...folderItemORM,
      meta: {
        isFavorite: folderItemORM.meta.isFavorite,
        title: folderItemORM.meta.title,
        updatedAt: folderItemORM.meta.updatedAt,
        createdAt: folderItemORM.meta.createdAt,
        assets: {},
        hasAutoUpdatedItem: false,
        hasOutdatedItem: false,
      },
      relations: {
        itemORM: draftCustomDeckORM,
      },
    }
  }

  const now = new Date().toISOString();

  return {
    model: {
      id: uuid(),
      customTitle: customTitle || 'Presentation Preview',
      type: FolderItemType.CUSTOM_DECK,
      itemId: uuid(),
      itemLastUpdatedAt: now,
      addedAt: now,
    },
    meta: {
      title: draftCustomDeckORM.model.title,
      isFavorite: false,
      updatedAt: now,
      createdAt: now,
      assets: {},
      hasAutoUpdatedItem: false,
      hasOutdatedItem: false,
    },
    type: ORMTypes.FOLDER_ITEM,
    relations: {
      itemORM: draftCustomDeckORM,
    },
  };
}

export const timeSinceLastSeen = (editMutex: EditMutex) => {
  const now = new Date()
  const lastUpdatedAt = new Date(editMutex.lastSeenAt)

  const diff = now.getTime() - lastUpdatedAt.getTime()
  const diffMinutes = Math.floor(diff / (1000 * 60))

  return diffMinutes
}

/** Provider instantiation/config */
const PresentationBuilderStateProvider: React.FC<PropsWithChildren<
  PresentationBuilderStateProviderProps
>> = (props) => {
  const globalBuilderState = useSelector((state: RootState) => state.PresentationBuilder);
  const { targetFolder, folderItemORM, customDeckId, variant, editorType, aiImport } = globalBuilderState;
  const history = useHistory();
  const documentVersions = useAllDocumentVersionMap();
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  const [saveError, setSaveError] = useState<string>();
  const [aiImportReady, setAIImportReady] = useState(!aiImport);
  const appliedImportRef = useRef<string>();
  const importedReferencesRef = useRef<Array<{ id: string; page_number: string }>>([]);
  const persistAIDraftRef = useRef<(folder: FolderORM) => Promise<void>>();
  const baselineDeckRef = useRef<CustomDeck>();
  const saveIdsRef = useRef(new Map<string, AIDeckSaveSession>());
  const { isOnline } = useAppSettings()
  const route = useCurrentPage({ exact: false })
  const isSharedFolders = route?.PATH.includes('shared_folders');
  const existingCustomDeck = useCustomDeckORMById(customDeckId || '');
  const requiresReview = !!existingCustomDeck?.meta.version.requiresReview;
  const isMinorUpdate = !requiresReview && !!folderItemORM?.meta.hasOutdatedItem;
  const dispatch = useDispatch()
  const userDocumentVersionSettingsMap = useSelector(selUserDocumentVersionSettingsMap);
  // We set to read only by default to allow the fetching of the lock
  const [isLocked, setIsLocked] = useState(!!existingCustomDeck && isOnline)
  const [builderMode, setBuilderMode] = useState<BuilderMode>(
    existingCustomDeck || aiImport ? 'customization' : 'selection',
  );
  const [title, setTitle] = useState<string | undefined>(folderItemORM?.meta.title ?? '')
  const [saveAttemptedWithoutTitle, setSaveAttemptedWithoutTitle] = useState<boolean>(false)
  const [selectedTargetItems, setSelectedTargetItems] = useState<string[]>([])
  const [numOfRequiredSlides, setNumOfRequiredSlides] = useState<number>(0)
  const [findReplacementVisible, setFindReplacementVisible] = useState(false);
  const [compliancePanelVisible, setCompliancePanelVisible] = useState(false);
  const initialTitleRef = useRef(title)

  /** If we're editing, we'll convert the CUSTOMDECKORM into our draft object **/
  const toCustomDeckPayloadGroups = (customDeckORM: CustomDeckORM) => {
    return customDeckORM.meta.customDeckGroups.map(
      (groupORM) => {
        const documentVersionORM = groupORM.pages[0].documentVersionORM;
        const groupStatus = getPayloadGroupStatus(documentVersionORM);

        const allAssociatedParents = getAssociatedParentIds(documentVersionORM?.meta.allPages ?? []);

        const pages = groupORM.pages.map(({ page }) => {
          return {
            ...getPageWithDocumentVersionHiddenState(page, documentVersionORM),
            parentIds: [...(allAssociatedParents.get(page.pageId) ?? [])],
          }
        })

        const mappedPayloadGroup: PayloadGroup = {
          id: groupORM.model.id,
          groupSrcId: groupORM.model.srcId,
          documentVersionORM,
          pages,
          visible: groupORM.model.visible,
          groupStatus,
          docAccessLevel: groupORM.model.docAccessLevel,
          name: groupORM.model.name,
          locked: groupORM.model.locked,
        }

        return mappedPayloadGroup
      },
    );
  }

  const [selectedGroups, setSelectedGroups] = useState<PayloadGroup[]>(() =>
    existingCustomDeck
      ? toCustomDeckPayloadGroups(existingCustomDeck)
      : [],
  );
  const hasNeedReviewGroup = useMemo(
    () => selectedGroups.some(group => group.groupStatus !== GroupStatus.ACTIVE), [selectedGroups])
  const [associatedParentsMap, setAssociatedParentsMap] = useState<AssociatedParentsMap>(new Map<string, boolean>())
  const initialSelectedGroupRef = useRef(selectedGroups)
  const [hiddenSlidesVisible, setHiddenSlidesVisible] = useState<boolean>(true)
  const [showReviewRequiredOnly, setShowReviewRequiredOnly] = useState<boolean>(hasNeedReviewGroup)
  const [displayPageSourceFile, setDisplayPageSourceFile] = useState<boolean>(true);
  const [activeReplacementGroup, setActiveReplacementGroup] = useState<PayloadGroup | undefined>();
  const pendingGroupsRevision = useRef<boolean>(false);
  const [disablePreview, setDisablePreivew] = useState<boolean>(false);
  const [showUnavailableMessage, setShowUnavailableMessage] = useState<boolean>(false);
  const [allContentPageDataVersions, setAllContentPageDataVersions] =
    useState<{[documentVersionId: string] : ContentPageData[]}>({});
  // const [enableSlideTextInsertion, setEnableSlideTextInsertion] = useState<boolean>(false);

  const featureFlags = useFeatureFlags(
    'BEAC_4251_slide_version_tracking',
    'BEAC_6389_slide_text_insertion',
    'BEAC_6655_custom_deck_usage',
  );

  function getPageWithDocumentVersionHiddenState<TPage extends Page>(
    page: TPage,
    documentVersionORM?: DocumentVersionORM,
  ) {
    // Builder previews combine source hidden state with this user's document/custom-deck overrides.
    const documentVersionPageSettingOverrides = documentVersionORM
      ? userDocumentVersionSettingsMap.documentVersionLevel[documentVersionORM.model.id]?.pageSettings
      : undefined;
    const customDeckPageSettingOverrides = existingCustomDeck
      ? userDocumentVersionSettingsMap.customDeckLevel[existingCustomDeck.model.id]?.pageSettings ?? []
      : undefined;
    const pageWithHiddenState = getPageWithHiddenSlideSettingsState({
      page,
      documentVersionPageSettings: documentVersionORM?.model.pageSettings ?? [],
      documentVersionPageSettingOverrides,
      customDeckPageSettingOverrides,
    });

    return pageWithHiddenState;
  }

  useEffect(() => {
    if (!existingCustomDeck || (aiImport && appliedImportRef.current)) {
      return;
    }

    setSelectedGroups((currentGroups) => {
      const updatedGroups = currentGroups.map(group => {
        const pages = group.pages.map(page => getPageWithDocumentVersionHiddenState(
          page,
          group.documentVersionORM,
        ));

        return {
          ...group,
          pages,
          visible: group.visible,
        };
      });

      return isEqual(currentGroups, updatedGroups) ? currentGroups : updatedGroups;
    });
  }, [
    existingCustomDeck,
    userDocumentVersionSettingsMap,
  ]);

  const getCustomDeckInheritedPageSettings = useCallback((
    groups: PayloadGroup[],
    existingCustomDeckPageSettings?: PageSettingOverride[],
  ): PageSettingOverride[] => {
    const inheritedPageSettings = groups.flatMap(group => {
      const documentVersionORM = group.documentVersionORM;
      if (!documentVersionORM) {
        return [];
      }

      return getCustomDeckInheritedPageSettingOverrides({
        pages: group.pages,
        documentVersionPageSettings: documentVersionORM.model.pageSettings ?? [],
        documentVersionPageSettingOverrides:
          userDocumentVersionSettingsMap.documentVersionLevel[documentVersionORM.model.id]?.pageSettings,
      });
    });
    const currentPageSettings = getCustomDeckCurrentPageSettingOverrides({ groups });
    const inheritedPageSettingIds = new Set(inheritedPageSettings.map(pageSetting => pageSetting.pageId));
    // Drop stale custom-deck settings for slides no longer inherited from the source deck.
    const relevantExistingCustomDeckPageSettings = existingCustomDeckPageSettings
      ?.filter(pageSetting => inheritedPageSettingIds.has(pageSetting.pageId));

    const mergedPageSettings = getMergedCustomDeckPageSettingOverrides({
      inheritedPageSettings: [
        ...inheritedPageSettings,
        ...(relevantExistingCustomDeckPageSettings ?? []),
      ],
      existingCustomDeckPageSettings: currentPageSettings,
    });

    return mergedPageSettings;
  }, [existingCustomDeck, userDocumentVersionSettingsMap]);

  const getCurrentCustomDeckPageSettings = useCallback((
    groups: PayloadGroup[] = selectedGroups,
  ): PageSettingOverride[] => {
    const existingCustomDeckPageSettings = existingCustomDeck
      ? userDocumentVersionSettingsMap.customDeckLevel[existingCustomDeck.model.id]?.pageSettings
      : undefined;
    const pageSettings = getCustomDeckInheritedPageSettings(groups, existingCustomDeckPageSettings);

    return pageSettings;
  }, [
    existingCustomDeck,
    getCustomDeckInheritedPageSettings,
    selectedGroups,
    userDocumentVersionSettingsMap,
  ]);

  const toggleHiddenPage = useCallback((groupId: string, pageId: string) => {
    setSelectedGroups(groups => groups.map(group => {
      if (group.id !== groupId) {
        return group;
      }

      const currentPage = group.pages.find(page => page.pageId === pageId);
      const nextIsHidden = currentPage?.sourceIsHidden && typeof currentPage.isHidden !== 'boolean'
        ? false
        : !currentPage?.isHidden;

      analytics?.track(`CUSTOM_SLIDE_${nextIsHidden ? 'HIDE' : 'UNHIDE'}`, {
        action: `SLIDE_${nextIsHidden ? 'HIDE' : 'UNHIDE'}`,
        category: 'CUSTOM',
        customDeckId: existingCustomDeck?.model.id,
        groupId: group.id,
        pageId,
        editorType,
      });

      return {
        ...group,
        visible: group.pages.length === 1 ? !nextIsHidden : group.visible,
        pages: group.pages.map(page => page.pageId === pageId
          ? {
            ...page,
            isHidden: nextIsHidden,
          }
          : page),
      };
    }));
  }, [editorType, existingCustomDeck, setSelectedGroups]);

  const toggleGroupVisibility = useCallback((groupId: string) => {
    setSelectedGroups(groups => groups.map(group => {
      if (group.id !== groupId) {
        return group;
      }

      const nextVisible = !group.visible;
      const action = nextVisible ? 'UNHIDE' : 'HIDE';
      group.pages.forEach(page => {
        analytics?.track(`CUSTOM_SLIDE_${action}`, {
          action: `SLIDE_${action}`,
          category: 'CUSTOM',
          customDeckId: existingCustomDeck?.model.id,
          groupId: group.id,
          pageId: page.pageId,
          editorType,
        });
      });

      return { ...group, visible: nextVisible };
    }));
  }, [editorType, existingCustomDeck, setSelectedGroups]);

  const persistExistingCustomDeckPageSettings = useCallback((
    groups: PayloadGroup[] = selectedGroups,
  ) => {
    if (!existingCustomDeck) {
      return;
    }

    const pageSettings = getCurrentCustomDeckPageSettings(groups);
    const existingSettings = userDocumentVersionSettingsMap.customDeckLevel[existingCustomDeck.model.id];

    // Empty settings mean the custom deck matches source visibility and needs no preference record.
    if (!pageSettings.length && !existingSettings) {
      return;
    }

    if (existingSettings) {
      dispatch(userDocumentVersionSettingsActions.updatePageSettings({
        userDocumentVersionSettings: existingSettings,
        pageSettings,
      }));
      return;
    }

    dispatch(userDocumentVersionSettingsActions.createUserDocumentVersionSettings({
      customDeckId: existingCustomDeck.model.id,
      pageSettings,
    }));
  }, [
    dispatch,
    existingCustomDeck,
    getCurrentCustomDeckPageSettings,
    selectedGroups,
    userDocumentVersionSettingsMap,
  ]);

  const currentVersionS3Mapping = useMemo(() => {
    return existingCustomDeck ? getS3KeysWithinCustomDeck(existingCustomDeck) : {}
  }, [existingCustomDeck])
  const latestVersionS3Mapping = useMemo(() => {
    return existingCustomDeck ? getS3KeysWithinCustomDeck(existingCustomDeck, true) : {}
  }, [existingCustomDeck])

  useEffect(() => {
    const getAllContentPageDataVersions = async () => {
      const responses = await fetchPagesJson({ ...currentVersionS3Mapping, ...latestVersionS3Mapping }, true)
      const mappingDocumentVersionIdtoContentPage =
        responses.reduce<{ [documentVersionId: string] : ContentPageData[]}>((acc, response) => {
          const documentVersionORM = getCurrentDocVer(response.documentVersionId)
          if (!documentVersionORM) {
            const errorMessage = 'Unable to find documentVerionORM from redux state'
            logger.customDeck.error(errorMessage)
            throw Error(errorMessage)
          }
          if (!acc[response.documentVersionId]) {
            const items = response.content.pages
              .map(page => {
                if (!page) {
                  logger.customDeck.warn('Page is null')
                  return null
                }
                const pageModel = documentVersionORM.relations.pages[page.number - 1]?.model
                if (!pageModel) {
                  logger.customDeck.verbose(`Page model not found for page number ${page.number}`)
                  return null
                }
                const rv: ContentPageData = {
                  content: page.content,
                  documentPageNumber: page.number,
                  presentationPageNumber: page.number,
                  speakerNotes: page.overrideSpeakerNotes ?? page.speakerNotes,
                  s3Key: detectArchivedFileKeyPath(documentVersionORM.model, pageModel, 'sm') || '',
                  title: page.overrideTitle ?? page.title,
                  htmlUrl: page.htmlUrl,
                  htmlPageImage: page.htmlPageImage,
                  recommendations: page.recommendations ?? [],
                  mapping: featureFlags.BEAC_4251_slide_version_tracking
                    ? (page.mapping ?? null)
                    : null,
                  pageId: pageModel.pageId,
                  enableSlideTextInsertion: !!page.enableSlideTextInsertion,
                }

                return rv
              })
              .filter((page): page is ContentPageData => page !== null)

            acc[response.documentVersionId] = items
          }
          return acc
        }, {})

      setAllContentPageDataVersions(mappingDocumentVersionIdtoContentPage)
    }
    getAllContentPageDataVersions()
  }, [currentVersionS3Mapping, latestVersionS3Mapping])

  const visibleGroupsMap = useMemo(() => {
    return selectedGroups.reduce<Record<string, boolean>>((acc, group) => {
      const isUnavailable = !!unavailableStatus[group.groupStatus]
      const isFindAndReplace = group.groupStatus === GroupStatus.MAJOR_UPDATE
      const isMinorWithAutoMappingSlides = group.groupStatus === GroupStatus.MINOR_UPDATE_WITH_AUTO_MAPPING_SLIDES
      const isSlideVisible = showReviewRequiredOnly
        ? (isUnavailable || isFindAndReplace || isMinorWithAutoMappingSlides)
        : (group.groupStatus !== GroupStatus.ACTIVE ||
          group.visible ||
          (hiddenSlidesVisible && !group.visible))

      acc[group.id] = isSlideVisible;
      return acc;
    }, {});
  }, [selectedGroups, hiddenSlidesVisible, showReviewRequiredOnly]);

  // When the visible group changed, we want to unselect everything
  useEffect(() => {
    setSelectedTargetItems([])
  }, [visibleGroupsMap])

  // We want to clear the number of required slide banner after 5 second
  useEffect(() => {
    if (numOfRequiredSlides !== 0) setTimeout(() => setNumOfRequiredSlides(0), 5000)
  }, [numOfRequiredSlides, setNumOfRequiredSlides])

  const {
    editorThumbnailSize,
    setEditorThumbnailSize,
    cycleEditorThumbnailSize,
    editorThumbnailDimensions,
  } = useEditorState(builderMode)

  const {
    selectorThumbnailSize,
    setSelectorThumbnailSize,
    cycleSelectorThumbnailSize,
    selectorThumbnailDimensions,
  } = useSelectorState()
  const currentUser = useCurrentUser()
  const currentUserId = currentUser.userProfile?.id

  const isCustomDeckLockedByOthers = useRef<boolean | undefined>(existingCustomDeck &&
    existingCustomDeck?.model.editMutex &&
    timeSinceLastSeen(existingCustomDeck.model.editMutex) < TIME_LAST_SEEN_MINUTES &&
    currentUserId !== existingCustomDeck.model.editMutex.userId).current
  /** Locking Mutex for Collaborative Editing */
  const intervalRef = useRef<NodeJS.Timeout>()

  const sendMutex = useCallback(() => {
    const timeStarted = new Date()
    existingCustomDeck && dispatch(customDeckActions.lockCustomDeck({
      customDeckId: existingCustomDeck.model.id!,
      timestarted: timeStarted.toISOString(),
      lock: true,
      currentUser: currentUser.userProfile!,
    }))
  }, [currentUser.userProfile?.id, existingCustomDeck])

  useEffect(() => {
    // We set readOnly to false when we see the deck is locked by the current user
    if (existingCustomDeck && existingCustomDeck.model.editMutex &&
      existingCustomDeck.model.editMutex.userId === currentUserId &&
      timeSinceLastSeen(existingCustomDeck.model.editMutex) < TIME_LAST_SEEN_MINUTES) {
      setIsLocked(false)
    }
  }, [existingCustomDeck])

  // useEffect(() => {
  //   if (
  //     featureFlags.BEAC_6389_slide_text_insertion &&
  //     existingCustomDeck &&
  //     existingCustomDeck.model.editMutex &&
  //     timeSinceLastSeen(existingCustomDeck.model.editMutex) < TIME_LAST_SEEN_MINUTES
  //   ) {
  //     setEnableSlideTextInsertion(true)
  //   }
  //   else setEnableSlideTextInsertion(false)
  // }, [existingCustomDeck, featureFlags.BEAC_6389_slide_text_insertion])

  useEffect(() => {
    if (existingCustomDeck && !isCustomDeckLockedByOthers) {
      sendMutex();
      intervalRef.current = setInterval(sendMutex, MUTEX_INTERVAL)
      return () => {
        intervalRef.current && clearInterval(intervalRef.current)
      }
    }
  }, [])

  /**
   * when the selected groups collection is mutated, we need to update the associated selected target items
   */
  useEffect(() => {
    if (selectedTargetItems.length) {
      const validSelectedTargetItems = selectedTargetItems.filter((id) => {
        const page = selectedGroups.find((group) => group.id === id)
        return !!page
      },
      )
      setSelectedTargetItems(validSelectedTargetItems)
    }

    if (pendingGroupsRevision.current) {
      const allGroupsAreValid = selectedGroups.every((group) => group.groupStatus === GroupStatus.ACTIVE);
      if (allGroupsAreValid) {
        onApplyFindAndReplace(selectedGroups, title || '');
      }
    }
  }, [selectedGroups])

  useEffect(() => {
    if (!aiImport || appliedImportRef.current === aiImport.requestId || !globalBuilderState.visible) return;
    if (!currentUserId || (customDeckId && !existingCustomDeck)) return;
    if (!featureFlags.BEAC_6655_custom_deck_usage) return;
    if (existingCustomDeck && (
      existingCustomDeck.model.createdBy !== currentUserId || existingCustomDeck.meta.version.requiresReview ||
      !targetFolder || targetFolder.model.createdBy !== currentUserId
    )) return;
    if (existingCustomDeck && isLocked) return;
    const original = existingCustomDeck ? toCustomDeckPayloadGroups(existingCustomDeck) : [];
    if (original.some(group => group.groupStatus !== GroupStatus.ACTIVE)) return;
    const result = buildAISearchDeckDraft({
      references: aiImport.references,
      versions: documentVersions,
      existingGroups: original,
      associatedParentsMap,
      dependencies: {
        createId: uuid,
        getPageWithDocumentVersionHiddenState: (page, version) => getPageWithHiddenSlideSettingsState({
          page: { ...page },
          documentVersionPageSettings: version?.model.pageSettings ?? [],
          documentVersionPageSettingOverrides: version
            ? userDocumentVersionSettingsMap.documentVersionLevel[version.model.id]?.pageSettings
            : undefined,
        }),
      },
    });
    if (!result.requestedCount || !result.addedCount) return;
    baselineDeckRef.current = existingCustomDeck?.model;
    importedReferencesRef.current = result.groups.slice(original.length).flatMap(group =>
      group.pages.map(page => ({ id: group.documentVersionORM!.model.id, page_number: String(page.number) })));
    initialSelectedGroupRef.current = original;
    appliedImportRef.current = aiImport.requestId;
    setSelectedGroups(result.groups);
    setAssociatedParentsMap(result.associatedParentsMap);
    setAIImportReady(true);
  }, [aiImport, existingCustomDeck, isLocked, currentUserId, documentVersions, globalBuilderState.visible,
    featureFlags.BEAC_6655_custom_deck_usage]);

  /** CONTEXT UTILITIES */
  const handleModeChange = useCallback((nextMode?: BuilderMode) => {
    // from selection mode or replace mode user can only go to customization mode
    // from customization mode user can either go to selection or replace depending on the button clicked
    setBuilderMode((p) => {
      if (nextMode) {
        return nextMode
      } else if (p === 'selection' || p === 'replace') {
        return 'customization'
      } else {
        // this is a catch all where the user is sent to the default mode
        return 'selection'
      }
    })
  }, [setBuilderMode])

  const openFindReplacement = useCallback((group: ModifiedPayloadGroup) => {
    setFindReplacementVisible(true)
    setActiveReplacementGroup(group)
    handleModeChange('replace')
  }, [setFindReplacementVisible, setActiveReplacementGroup, handleModeChange])

  const clearSlides = () => {
    selectedGroups.length &&
      dispatch(
        DNAModalActions.setModal(
          {
            isVisible: true,
            allowBackdropCancel: true,
            component: (props) => (
              <ClearSlides
                {...props}
                onClear={() => setNumOfRequiredSlides(0)}
                setSelectedGroups={setSelectedGroups}
              />
            ),
          },
        ),
      )
  }

  const onPreview = (pageNumber?: number, openedTab?: SidebarOptions): void => {
    const draftFolderItemORM = toFolderItemORM(selectedGroups, title, existingCustomDeck, folderItemORM);

    // TODO: add type guard and remove as assertion here once typings cleanup from #1708 has been merged
    dispatch(
      contentPreviewModalActions.setModalVisibility({
        documentVersionId: (draftFolderItemORM.relations.itemORM.model as CustomDeck)
          .groups[0]?.pages[0].documentVersionId,
        folderItemORM: draftFolderItemORM,
        isOpen: true,
        content: draftFolderItemORM,
        pageNumber,
        openedTab,
      },
      ))
  };

  const savePresentation = () => {
    if (savingRef.current || (aiImport && !aiImportReady)) return;
    if (selectedGroups.length) {
      if (!title) {
        setSaveAttemptedWithoutTitle(true);
        return;
      }
      if (hasRequiredHiddenSlides(selectedGroups, associatedParentsMap)) {
        openRequiredHiddenSlidesConfirmation()
        return
      }

      createOrUpdatePresentation()
      setSaveAttemptedWithoutTitle(false);
    }
  }

  // For this function we are only saving the slides (not title)
  const saveFindReplacement = useCallback((
    updatedPayloadGroups: PayloadGroup[],
    replaceGroupDraft: ReplaceGroupDraft[],
    callBack?: () => void,
  ) => {
    // Reset the initial value
    initialSelectedGroupRef.current = updatedPayloadGroups
    setSaveAttemptedWithoutTitle(false);
    if (!existingCustomDeck) {
      const errorText = 'Cannot find existing custom deck when trying to save replacement'
      logger.customDeck.updates.findAndReplace.error(errorText)
      throw new Error(errorText)
    }

    const isValidCustomDeck = folderItemORM && targetFolder

    if (!isValidCustomDeck) {
      const errorText = 'Cannot find folderItemORM or targetFolder when trying to save replacement'
      logger.customDeck.updates.findAndReplace.error(errorText)
      throw new Error(errorText)
    }

    const isCreatorOfDeck = existingCustomDeck.model.createdBy === currentUserId
    const isCollaborative = !!isSharedFolders || !isCreatorOfDeck
    const newCustomDeckGroup = payloadGroupsToCustomDeckGroup(updatedPayloadGroups, existingCustomDeck)
    persistExistingCustomDeckPageSettings(updatedPayloadGroups);

    if (isCollaborative) {
      try {
        dispatch(customDeckActions.updateCustomDeckByAPI({
          customDeck: existingCustomDeck.model,
          title: existingCustomDeck.model.title,
          groups: newCustomDeckGroup,
          folder: targetFolder,
          folderItem: folderItemORM,
          currentUser: currentUser.userProfile!,
        }))
      } catch (e) {
        const errorText = 'Error updating custom deck in collaborative mode'
        logger.customDeck.updates.findAndReplace.error(errorText)
        throw new Error(errorText)
      }
    } else {
      try {
        dispatch(folderActions.updateCustomDeck(
          targetFolder,
          folderItemORM,
          existingCustomDeck.model,
          newCustomDeckGroup,
          existingCustomDeck.model.title,
        ))
      } catch (e) {
        const errorText = 'Error updating custom deck in non-collaborative mode'
        logger.customDeck.updates.findAndReplace.error(errorText)
        throw new Error(errorText)
      }
    }

    // Update userNotation
    const userNotations = existingCustomDeck.relations.userNotations
    const notations = userNotations?.notation
    if (notations) {
      const pageIdMap = notations.reduce<{[oldPageId: string]: string}>((acc, curr) => {
        const oldPageId = curr.pageId
        const matchingPageGroupDraft = replaceGroupDraft.find(group => {
          return group.status === GroupDraftStatus.REPLACED && group.pages.find(page => page.pageId === oldPageId)
        })
        if (matchingPageGroupDraft) {
          const newPageId = matchingPageGroupDraft.groupReplacement?.pages[0].pageId
          if (newPageId && !acc[oldPageId]) acc[oldPageId] = newPageId
        }
        return acc
      }, {})

      !isEmpty(pageIdMap) && dispatch(userNotationsActions.updateNotationsPageId({
        userNotations,
        pageIdMap,
      }))
    }

    callBack?.()
  }, [existingCustomDeck])

  const persistAIDraft = async (folder: FolderORM) => {
    const user = currentUser.userProfile;
    if (!user || !title || !aiImportReady || !featureFlags.BEAC_6655_custom_deck_usage) {
      throw new Error('This presentation is not ready to save.');
    }
    const allContentCached = selectedGroups.every(group =>
      !!documentVersions[group.documentVersionORM?.model.id ?? '']?.meta.assets.isContentCached);
    if (!canSaveGroups(selectedGroups, isLocked, isOnline, allContentCached)) {
      throw new Error('This presentation is no longer available for saving.');
    }
    const retainedPages = new Set(selectedGroups.flatMap(group => group.pages.map(page =>
      `${group.documentVersionORM?.model.id}:${page.number}`)));
    const resolution = resolveAISearchDeckReferences(importedReferencesRef.current.filter(reference =>
      retainedPages.has(`${reference.id}:${reference.page_number}`)), documentVersions);
    if (resolution.exclusions.length) throw new Error('Source slides changed. Reopen the search before saving.');
    if (existingCustomDeck?.meta.version.requiresReview) throw new Error('Review the presentation before saving.');
    let ids = saveIdsRef.current.get(folder.model.id);
    if (!ids) {
      ids = { folderItemId: uuid() };
      saveIdsRef.current.set(folder.model.id, ids);
    }
    await saveAIDeck({
      session: ids,
      folderId: folder.model.id,
      userId: user.id,
      tenantId: user.tenantId,
      title,
      groups: payloadGroupsToCustomDeckGroup(selectedGroups, existingCustomDeck),
      pageSettings: getCurrentCustomDeckPageSettings(selectedGroups),
      existingDeck: baselineDeckRef.current,
      existingFolderItemId: folderItemORM?.model.id,
      existingSettingsId: existingCustomDeck
        ? userDocumentVersionSettingsMap.customDeckLevel[existingCustomDeck.model.id]?.id
        : undefined,
    });
    analytics?.track('CUSTOM_SAVE', {
      action: 'SAVE',
      category: 'CUSTOM',
      customDeckId: existingCustomDeck?.model.id ?? ids.deck?.id,
      editorType,
      source: 'AI_SEARCH',
    });
  };
  persistAIDraftRef.current = persistAIDraft;

  const createOrUpdatePresentation = async () => {
    if (aiImport) {
      if (savingRef.current || !aiImportReady) return;
      if (!targetFolder) {
        dispatch(DNAModalActions.setModal({
          isVisible: true,
          allowBackdropCancel: false,
          component: props => (<SelectFolder
            {...props}
            itemORM={payloadGroupsToCustomDeckGroup(selectedGroups, existingCustomDeck)}
            title={title || ''}
            pageSettings={getCurrentCustomDeckPageSettings(selectedGroups)}
            personalFoldersOnly
            onSaveToFolder={folder => persistAIDraftRef.current!(folder)}
            onDone={() => closePresentationBuilder(true)}
          />),
        }));
        return;
      }
      savingRef.current = true;
      setIsSaving(true);
      setSaveError(undefined);
      try {
        await persistAIDraft(targetFolder);
        closePresentationBuilder(true);
      } catch (error) {
        setSaveError(error instanceof Error ? error.message : 'Could not save. Please try again.');
      } finally {
        savingRef.current = false;
        setIsSaving(false);
      }
      return;
    }
    if (existingCustomDeck) {
      const isOwnCustomDeck = existingCustomDeck.model.createdBy === currentUser.userProfile?.id
      persistExistingCustomDeckPageSettings(selectedGroups);
      folderItemORM &&
      targetFolder &&
      title &&
      !isSharedFolders && isOwnCustomDeck &&
      dispatch(folderActions.updateCustomDeck(
        targetFolder,
        folderItemORM,
        existingCustomDeck.model,
        payloadGroupsToCustomDeckGroup(selectedGroups, existingCustomDeck),
        title,
      ))

      // collaboration mode
      folderItemORM &&
      targetFolder &&
      title &&
      (isSharedFolders || !isOwnCustomDeck) &&
      dispatch(customDeckActions.updateCustomDeckByAPI({
        customDeck: existingCustomDeck.model,
        title: title,
        groups: payloadGroupsToCustomDeckGroup(selectedGroups, existingCustomDeck),
        folder: targetFolder,
        folderItem: folderItemORM,
        currentUser: currentUser.userProfile!,
      },
      ))
      const shouldUpdateFolderItem = targetFolder && folderItemORM && existingCustomDeck.model.title !== title
      shouldUpdateFolderItem && dispatch(sharedFolderActions.updateItems({
        folder: targetFolder,
        action: UpdateType.UPDATE_FOLDER_ITEM_TITLE,
        folderItems: [{ ...folderItemORM, model: { ...folderItemORM.model, customTitle: title } }],
      }))
      closePresentationBuilder(true)
    } else {
      const customDeckPageSettings = getCurrentCustomDeckPageSettings(selectedGroups);
      if (!targetFolder) {
        const customDeck = payloadGroupsToCustomDeckGroup(selectedGroups, existingCustomDeck)
        title && dispatch(DNAModalActions.setModal({
          isVisible: true,
          allowBackdropCancel: true,
          component: (props) => (
            <SelectFolder
              {...props}
              itemORM={customDeck}
              pageSettings={customDeckPageSettings}
              title={title}
              onDone={() => closePresentationBuilder(true)}
            />
          ),
        }),
        )
      }
      else {
        const isSharedFolder = isSharedEditableFolder(targetFolder)
        if (!title) return
        if (!isSharedFolder) {
          dispatch(folderActions.createCustomDeck(
            targetFolder,
            payloadGroupsToCustomDeckGroup(selectedGroups, existingCustomDeck),
            title,
            undefined,
            customDeckPageSettings))
        }
        else {
          dispatch(sharedFolderActions.createCustomDeckInFolder(
            {
              targetFolder,
              selectedSlides: payloadGroupsToCustomDeckGroup(selectedGroups, existingCustomDeck),
              pageSettings: customDeckPageSettings,
              title : title || '',
            },
          ))
        }
        closePresentationBuilder(true)
      }
    }
  }

  const cancelPresentation = () => {
    if (savingRef.current) return;
    const hasChanges = !isEqual(initialSelectedGroupRef.current, selectedGroups) || initialTitleRef.current !== title
    if (hasChanges) {
      openCloseConfirmation()
      return
    }

    !aiImport && customDeckId &&
      !isCustomDeckLockedByOthers && dispatch(customDeckActions.lockCustomDeck({
      customDeckId: customDeckId,
      lock: false,
      timestarted: '',
      currentUser: currentUser.userProfile!,
    }))
    closePresentationBuilder(false)
  }

  const closePresentationBuilder = (saved = false) => {
    if (aiImport && customDeckId && !isCustomDeckLockedByOthers && currentUser.userProfile) {
      dispatch(customDeckActions.lockCustomDeck({
        customDeckId, lock: false, timestarted: '', currentUser: currentUser.userProfile,
      }));
    }
    dispatch(presentationBuilderActions.closePresentationBuilder());
    if (shouldNavigateToFoldersAfterAISearchClose(!!aiImport, saved)) {
      history.push(ROUTES.UI2_FOLDERS_MY_FOLDERS.PATH);
    }
  };

  const openRequiredHiddenSlidesConfirmation = () => {
    dispatch(
      DNAModalActions.setModal(
        {
          isVisible: true,
          allowBackdropCancel: true,
          component: (props) => (
            <RequiredSlidesHiddenConfirmation
              onSave={createOrUpdatePresentation}
              {...props}
            />
          ),
        },
      ),
    )
  }

  const onApplyFindAndReplace = (payloadGroups: PayloadGroup[], newTitle?: string) => {
    newTitle && setTitle(newTitle);
    setBuilderMode('customization')
    const { groups, numRequiredSlidesAdded } = validatePayloadGroups(payloadGroups)

    const allAssociatedParents: {[key: string]: string[]} = {}
    groups.forEach(group => {
      group.documentVersionORM?.meta.allPages.forEach(page => {
        page.linkedSlides?.forEach(slideId => {
          if (allAssociatedParents[slideId] && !allAssociatedParents[slideId].includes(page.pageId)) {
            allAssociatedParents[slideId].push(page.pageId)
          }
          else allAssociatedParents[slideId] = [page.pageId]
        })
      })
    })
    const modifiedPayloadGroups:ExtendedPagePayloadGroup[] = groups.map(group => {
      const extendedPages:ExtendedPage[] = group.pages.map(page => {
        const extenedPage:ExtendedPage = {
          ...page,
          parentIds: allAssociatedParents[page.pageId],
        }
        return extenedPage
      })
      const modifiedPayloadGroup:ExtendedPagePayloadGroup = { ...group, pages: extendedPages }
      return modifiedPayloadGroup
    })
    setSelectedGroups(modifiedPayloadGroups)
    setNumOfRequiredSlides(numRequiredSlidesAdded)
    return modifiedPayloadGroups
  };

  const validatePayloadGroups = (existingPayloadGroups: PayloadGroup[]) => {
    const result = validatePayloadGroupsRule(existingPayloadGroups, {
      createId: uuid,
      getPageWithDocumentVersionHiddenState,
    }, pendingGroupsRevision.current)
    pendingGroupsRevision.current = result.pendingGroupsRevision
    return result
  }

  const onCloseHandler = () => {
    batch(() => {
      !aiImport && customDeckId && dispatch(customDeckActions.lockCustomDeck({
        customDeckId: customDeckId,
        lock: false,
        timestarted: '',
        currentUser: currentUser.userProfile!,
      }))
      closePresentationBuilder(false)
    })
  }

  const openCloseConfirmation = () => {
    dispatch(
      DNAModalActions.setModal(
        {
          isVisible: true,
          allowBackdropCancel: true,
          component: (props) => <CloseConfirmation {...props} onClose={onCloseHandler} />,
        },
      ),
    )
  }

  const onRemoveGroup = useCallback((group: PayloadGroup) => {
    group.pages.forEach((page) => {
      analytics?.track('CUSTOM_SLIDE_REMOVED', {
        action: 'SLIDE_REMOVED',
        category: 'CUSTOM',
        customDeckId: existingCustomDeck?.model.id,
        groupId: group.id,
        pageId: page.pageId,
        editorType,
      });
    })
    setSelectedGroups((groups) => groups.filter((grp) => grp.id !== group.id))
  }, [editorType, existingCustomDeck, setSelectedGroups])

  const onRemoveAllUnavailableGroups = useCallback(() => {
    setSelectedGroups((groups) => {
      return groups.filter((grp) => {
        const isUnavailable = !!unavailableStatus[grp.groupStatus]
        if (isUnavailable) {
          grp.pages.forEach((page) => {
            analytics?.track('CUSTOM_SLIDE_REMOVED', {
              action: 'SLIDE_REMOVED',
              category: 'CUSTOM',
              customDeckId: existingCustomDeck?.model.id,
              groupId: grp.id,
              pageId: page.pageId,
              editorType: editorType,
            });
          })
        }
        return !isUnavailable
      })
    })
  }, [editorType, existingCustomDeck, setSelectedGroups])

  const onSeparateGroup = useCallback((group: PayloadGroup) => {
    /** Filter out current group from selected groups */
    const updatedSelectedGroups = selectedGroups.filter(({ id }) => group.id !== id)

    /** Add slides from current group as individual groups */
    const separatedSlides = group.pages.map(page => {
      const newPayloadGroup: PayloadGroup = {
        id: uuid(),
        pages: [page],
        visible:true,
        groupStatus: GroupStatus.ACTIVE,
        docAccessLevel: group.docAccessLevel,
        documentVersionORM: group.documentVersionORM,
      }
      return newPayloadGroup
    })

    updatedSelectedGroups.push(...separatedSlides)

    setSelectedGroups(updatedSelectedGroups)

    // ANALYTIC TRACKING: ungrouped named group
    analytics?.track('GROUP_UNGROUP', {
      customDeckId: existingCustomDeck?.model.id,
      srcGroupId: group.id,
    })
  }, [selectedGroups, setSelectedGroups, existingCustomDeck])

  /**
 * This function check if this group has association to determin if the remove button should show or not
 * If page within group has associated parent(s) and there is no duplicate of page, then user cannot remove the group
 */
  const isSingleAndHasAssociation = useCallback((group: ModifiedPayloadGroup):boolean => {
    return isSingleAndHasAssociationRule(group, selectedGroups, associatedParentsMap)
  }, [selectedGroups, associatedParentsMap])

  const canRemoveGroup = useCallback((group: PayloadGroup): boolean => {
    return canRemoveGroupRule(group, selectedGroups)
  }, [selectedGroups])

  /**
   * `autoUpdateMapping` is an object where both the keys and values are strings.
   * It records the mapping of auto-updated documents based on the following rules:
   *
   * - **Single Slide Mapping**: If a single slide has a mapping, the key is the current slide's `pageId`, and the value is the mapped `pageId`.
   * - **Group Mapping**: For minor update documents, the group will be auto-carried over. The key is the current `groupId`, and the value is the mapped source `groupId`.
   * - **NOTE:** Skip versions is not supported for auto updates.
   */
  const autoUpdateMapping = useMemo(() => {
    return selectedGroups.reduce<Record<string, string>>((acc, group) => {
      const isSingleSlideGroup = group.pages.length === 1;
      if (isSingleSlideGroup) {
        const newReplacementPageId = getValidMappedSingleSlidesGroup(group, allContentPageDataVersions)
        if (newReplacementPageId) return { ...acc, [group.pages[0].pageId]: newReplacementPageId }
      } else if (!isSingleSlideGroup && isValidMinorUpdatedMultiSlidesGroup(group) && group.groupSrcId) {
        return { ...acc, [group.id]: group.groupSrcId }
      }
      return acc
    }, {})
  }, [selectedGroups, allContentPageDataVersions])

  // DETERMINES WHICH GROUPS NEED TO CHANGE THEIR STATUS TO "MINOR_UPDATE_WITH_AUTO_MAPPING_SLIDES"
  // TO DO SO, THE autoUpdateMapping MUST BE SET TO CHECK THE "PRE-MAPPED" SLIDES.
  useEffect(() => {
    if (Object.keys(autoUpdateMapping).length && selectedGroups.length) {
      let hasAGroupStatusChanged = false;
      const updatedGroups = selectedGroups.map((group) => {
        if (group.groupStatus === GroupStatus.ACTIVE) {
          const hasAutoMappingSlides = group.pages.length > 1
            ? !!autoUpdateMapping[group.id]
            : group.pages.every(({ pageId }) => !!autoUpdateMapping[pageId]);

          if (hasAutoMappingSlides) {
            hasAGroupStatusChanged = true;
            return {
              ...group,
              groupStatus: GroupStatus.MINOR_UPDATE_WITH_AUTO_MAPPING_SLIDES,
            };
          }
        }
        return group;
      });

      hasAGroupStatusChanged && setSelectedGroups(updatedGroups);
    }
  }, [autoUpdateMapping, allContentPageDataVersions]);

  const acceptAutoUpdateSlides = useCallback(() => {
    const updatedGroups: PayloadGroup[] = [];
    const replaceGroups = assignReplacementGroups(selectedGroups)

    replaceGroups.forEach((replaceGroup, index) => {
      const currentGroup = selectedGroups[index];
      const isSingleSlideGroup = replaceGroup.pages.length === 1;
      const latestPublishedDocumentVersionORM =
        currentGroup.documentVersionORM?.relations.documentORM.relations.version.latestPublishedDocumentVersionORM;
      if (!latestPublishedDocumentVersionORM) {
        // IF CANNOT FIND THE LATEST PUBLISHED DOCUMENT VERSION, THIS DOCUMENT MIGHT BE DELETED
        // eslint-disable-next-line max-len
        logger.customDeck.debug(`The latest version of this document ${currentGroup.documentVersionORM?.model.id} not found, it might be deleted`)
        updatedGroups.push(currentGroup)
      } else if (replaceGroup.status !== GroupDraftStatus.GROUP_REMOVED) {
        // [NOTE]: currently we do not support mapping for groups with multiple pages for major updated documents
        if (isSingleSlideGroup) {
          const newReplacementPageId = autoUpdateMapping[replaceGroup.pages[0].pageId]
          const newReplacementPage = latestPublishedDocumentVersionORM.relations.pages.find(page => {
            return page.model.pageId === newReplacementPageId
          })?.model

          if (newReplacementPage) {
            const replacementPages = [
              getPageWithDocumentVersionHiddenState(newReplacementPage, latestPublishedDocumentVersionORM),
            ]
            const replacementGroup = {
              id: uuid(),
              groupSrcId: newReplacementPage?.pageId,
              documentVersionORM: latestPublishedDocumentVersionORM,
              pages: replacementPages,
              visible: getCustomDeckDefaultGroupVisibility(replacementPages),
              groupStatus: GroupStatus.ACTIVE,
              docAccessLevel: latestPublishedDocumentVersionORM.relations.documentORM.model.accessLevel,
            }
            updatedGroups.push(replacementGroup)
            replaceGroup.groupReplacement = replacementGroup
            replaceGroup.status = GroupDraftStatus.REPLACED
          } else {
            updatedGroups.push(currentGroup)
          }
        } else {
          const newReplacementGroupId = autoUpdateMapping[replaceGroup.groupId]
          const newReplacementGroup = latestPublishedDocumentVersionORM.relations.pageGroups
            ?.find(pageGroup => pageGroup.model.id === newReplacementGroupId);

          if (newReplacementGroup) {
            const replacementPages = newReplacementGroup.relations.pages
              .map(pageORM => getPageWithDocumentVersionHiddenState(
                pageORM.model,
                latestPublishedDocumentVersionORM,
              ))
            const replacementGroup = {
              id: uuid(),
              groupSrcId: newReplacementGroup.model.id,
              documentVersionORM: latestPublishedDocumentVersionORM,
              pages: replacementPages,
              visible: getCustomDeckDefaultGroupVisibility(replacementPages),
              groupStatus: GroupStatus.ACTIVE,
              docAccessLevel: latestPublishedDocumentVersionORM.relations.documentORM.model.accessLevel,
            }
            updatedGroups.push(replacementGroup)
            replaceGroup.groupReplacement = replacementGroup
            replaceGroup.status = GroupDraftStatus.REPLACED
          } else {
            updatedGroups.push(currentGroup)
          }
        }
      }
    });

    const updatedPayloadGroups = onApplyFindAndReplace(updatedGroups);
    saveFindReplacement(updatedPayloadGroups, replaceGroups)
    analytics?.track('CUSTOM_REPLACE_ACCEPT_ALL', {
      action: 'REPLACE',
      category: 'CUSTOM',
      customDeckId: existingCustomDeck?.model.id,
    });
  }, [selectedGroups, allContentPageDataVersions, autoUpdateMapping, existingCustomDeck])

  const contextValue: PresentationBuilderStateType = {
    isSaving,
    saveError,
    onApplyFindAndReplace,
    clearSlides,
    closePresentationBuilder,
    savePresentation,
    saveFindReplacement,
    builderMode,
    setBuilderMode,
    title,
    setTitle,
    saveAttemptedWithoutTitle,
    setSaveAttemptedWithoutTitle,
    numOfRequiredSlides,
    setNumOfRequiredSlides,
    selectedGroups,
    setSelectedGroups,
    toggleHiddenPage,
    toggleGroupVisibility,
    visibleGroupsMap,
    associatedParentsMap,
    setAssociatedParentsMap,
    hiddenSlidesVisible,
    setHiddenSlidesVisible,
    showReviewRequiredOnly,
    setShowReviewRequiredOnly,
    displayPageSourceFile,
    setDisplayPageSourceFile,
    handleModeChange,

    onPreview,
    editorThumbnailSize,
    setEditorThumbnailSize,
    cycleEditorThumbnailSize,
    editorThumbnailDimensions,

    selectorThumbnailSize,
    setSelectorThumbnailSize,
    cycleSelectorThumbnailSize,
    selectorThumbnailDimensions,

    cancelPresentation,
    activeReplacementGroup,
    setActiveReplacementGroup,
    customDeck: existingCustomDeck,
    editorType,
    variant,
    isLocked: isLocked || isSaving || (!!aiImport && !aiImportReady),
    isCustomDeckLockedByOthers,

    selectedTargetItems,
    setSelectedTargetItems,
    disablePreview,
    setDisablePreivew,
    showUnavailableMessage,
    setShowUnavailableMessage,
    findReplacementVisible,
    setFindReplacementVisible,
    openFindReplacement,
    hasNeedReviewGroup,
    compliancePanelVisible,
    setCompliancePanelVisible,

    onRemoveGroup,
    onRemoveAllUnavailableGroups,
    onSeparateGroup,
    isSingleAndHasAssociation,
    canRemoveGroup,

    targetFolder,

    allContentPageDataVersions,
    setAllContentPageDataVersions,
    getPageWithDocumentVersionHiddenState,
    acceptAutoUpdateSlides,
    autoUpdateMapping,
    isMinorUpdate,
    // enableSlideTextInsertion,
  }

  return (
    <PresentationBuilderStateContext.Provider value={contextValue}>
      {props.children}
    </PresentationBuilderStateContext.Provider>
  )
}

PresentationBuilderStateProvider.displayName = 'PresentationBuilderStateProvider'

/** Hook for utilizing presentation builder state values/methods. Also
 * handles guarding against using context outside of the provider */
export function usePresentationBuilderState() {
  const context = useContext(PresentationBuilderStateContext)
  if (!context) {
    throw new Error('usePresentationBuilderState must be used within the PresentationBuilderStateProvider')
  }
  return context;
}

export default PresentationBuilderStateProvider
